// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import {
  type Shipment,
  useEvolvePost,
  type WorkOrder,
  type WorkOrderId,
  type WorkOrderItem,
  type WorkOrderItemId,
} from '@evolvemep/api-ts';
import { Divider, Flex, Text } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useFormModal } from 'components/Mantine/Modals/FormModal';
import { isNil } from 'helpers/isNotNil';
import { ShipmentItemsSelector } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/ShipmentItemsSelector';
import { WorkOrderPickerPanel } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/WorkOrderPickerPanel';

export type ShipmentItemsFormValues = {
  items: SelectedShipmentItem[];
  allSelectedWorkOrderIds: WorkOrderId[];
};

type Props = {
  workOrder?: WorkOrder;
  initialValues?: ShipmentItemsFormValues;
  shipment: Shipment;
  onItemsAdded: () => void;
};

export type SelectedShipmentItem = {
  workOrderItemId: WorkOrderItemId;
  workOrderId: WorkOrderId;
  name: WorkOrderItem['name'];
  quantity: number;
};

export const AddShipmentItemsForm = ({ workOrder: parentWorkOrder, initialValues, shipment, onItemsAdded }: Props) => {
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<WorkOrderId | null>(null);
  const workOrderId = parentWorkOrder?.workOrderId ?? selectedWorkOrderId;

  const { apiCall: addItemsApiCall } = useEvolvePost(`shop/shipment/${shipment.shipmentId}/item`);

  const form = useForm<ShipmentItemsFormValues>({
    initialValues: {
      items: initialValues?.items ?? [],
      allSelectedWorkOrderIds: initialValues?.allSelectedWorkOrderIds ?? [],
    },
    validate: {
      items: (v, values) => v.length === 0 && values.allSelectedWorkOrderIds.length === 0,
    },
  });

  const onSubmit = async ({ items, allSelectedWorkOrderIds }: typeof form.values) => {
    await addItemsApiCall({
      items: items.map(({ workOrderItemId, quantity }) => ({
        workOrderItemId,
        allocatedQuantity: quantity,
      })),
      workOrderIds: allSelectedWorkOrderIds,
    });
    onItemsAdded();
  };

  useFormModal(form, onSubmit);

  return (
    <Flex gap="md" flex={1} mih="100%">
      {isNil(parentWorkOrder) && (
        <>
          <WorkOrderPickerPanel
            shipment={shipment}
            selectedItems={form.values.items}
            allSelectedWorkOrderIds={form.values.allSelectedWorkOrderIds}
            selectedWorkOrder={selectedWorkOrderId}
            onSelectWorkOrder={setSelectedWorkOrderId}
          />

          <Divider orientation="vertical" />
        </>
      )}

      {isNil(workOrderId) ? (
        <Flex flex={1} align="center" justify="center">
          <Text c="dimmed" fz="sm">
            Select a work order to view its items.
          </Text>
        </Flex>
      ) : (
        <ShipmentItemsSelector key={workOrderId} form={form} workOrderId={workOrderId} />
      )}
    </Flex>
  );
};
